import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import LoadingScreen from "../components/LoadingScreen";
import Card from "../components/Card";
import TabBar from "../components/TabBar";
import Button from "../components/Button";
import Dropdown from "../components/Dropdown";
import Modal from "../components/Modal";
import ChartSettings from "../components/ChartSettings";
import DownloadSettings from "../components/DownloadSettings";
import ShareSettings from "../components/ShareSettings";
import ShareEmailModal from "../components/ShareEmailModal";
import DateRangePicker from "../components/DateRangePicker";
import { loadFilterSection, saveFilterSection } from "../utils/filterStorage";
import { formatNumber } from "../utils/formatNumber";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut, Pie } from "react-chartjs-2";
import "./styles.css";

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

// Importar iconos
import iconCalendar from "../assets/icons/calendar.svg";
import iconDownload from "../assets/icons/download.svg";
// import iconShare from '../assets/icons/share.svg'
import iconCog from "../assets/icons/settings.svg";
import iconTriangleUp from "../assets/icons/triangle-up.svg";
import iconTriangleDown from "../assets/icons/triangle-down.svg";

export default function Usuarios() {
    const globalFilters = loadFilterSection("global") || {};
    const usuariosFilters = loadFilterSection("usuarios") || {};
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [provincias, setProvincias] = useState([]);
    const [cotos, setCotos] = useState([]);

    // Datos iniciales del controlador
    const [initialDistribucionRol, setInitialDistribucionRol] = useState([]);
    const [initialUsuariosData, setInitialUsuariosData] = useState({
        total_usuarios: 40433,
        total_cazadores: 36454,
        porcentaje_cazadores: 90,
    });
    const [initialCotosData, setInitialCotosData] = useState({
        total_cotos: 2100,
        total_titulares: 8080,
        porcentaje_titulares: 20,
    });
    const [initialCotosPorTitular, setInitialCotosPorTitular] = useState([]);
    const [initialAccesosData, setInitialAccesosData] = useState({
        sesiones: 75094,
        visitas: 452314,
    });
    const [initialDistribucionAccesos, setInitialDistribucionAccesos] = useState([]);
    const [initialSolicitudesData, setInitialSolicitudesData] = useState({
        total_solicitudes: 348,
        diferencia_semanal: 14,
        tendencia: "up",
    });
    const [initialDistribucionSolicitudes, setInitialDistribucionSolicitudes] = useState([]);

    // Cargar datos iniciales
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [provinciasRes, cotosRes, usuariosInicialRes] = await Promise.all([
                api.get('/provincias'),
                api.get('/cotos'),
                api.get('/usuarios')
            ]);

            setProvincias((provinciasRes.data || [])
                .filter((p) => {
                    const label = typeof p === 'string' ? p : (p.label ?? p.nombre ?? '');
                    return !label.toLowerCase().includes('demo');
                })
                .map((p) => {
                    if (typeof p === 'string') return p.toUpperCase();
                    const label = p.label ?? p.nombre ?? '';
                    return { ...p, label: label.toUpperCase() };
                }));
            setCotos(cotosRes.data || []);
            
            // Cargar datos iniciales de usuarios desde el endpoint principal
            if (usuariosInicialRes.data) {
                const data = usuariosInicialRes.data;
                setUsuariosData(data.usuariosData || initialUsuariosData);
                setCotosData(data.cotosData || initialCotosData);
                setSolicitudesData(data.solicitudesData || initialSolicitudesData);
                setDistribucionRol(data.distribucionRol || initialDistribucionRol);
                setCotosPorTitular(data.cotosPorTitular || initialCotosPorTitular);
                setDistribucionSolicitudes(data.distribucionSolicitudes || initialDistribucionSolicitudes);
                setAccesosData(data.accesosData || initialAccesosData);
                setDistribucionAccesos(data.distribucionAccesos || initialDistribucionAccesos);
            }
        } catch (err) {
            // No capturar en Sentry si es un error 401 (no autorizado)
            if (err.response?.status !== 401) {
                Sentry.captureException(new Error('Error loading initial data: ' + err.message));
            }
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Estados
    const [activeTab, setActiveTab] = useState("usuarios");
    const [selectedProvincia, setSelectedProvincia] = useState(
        globalFilters.selectedProvincia || [],
    );
    const [selectedCoto, setSelectedCoto] = useState(
        globalFilters.selectedCoto || [],
    );
    const [selectedPeriod, setSelectedPeriod] = useState(
        usuariosFilters.selectedPeriod || "mensual",
    );
    const [selectedPeriodSolicitudes, setSelectedPeriodSolicitudes] =
        useState(usuariosFilters.selectedPeriodSolicitudes || "mensual");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalCotosTitularOpen, setIsModalCotosTitularOpen] =
        useState(false);
    const [isModalAccesosOpen, setIsModalAccesosOpen] = useState(false);
    const [isModalSolicitudesOpen, setIsModalSolicitudesOpen] = useState(false);
    const [chartTypeRol, setChartTypeRol] = useState("pie");
    const [chartTypeCotosTitular, setChartTypeCotosTitular] =
        useState("barras");
    const [chartTypeAccesos, setChartTypeAccesos] = useState("lineal");
    const [chartTypeSolicitudes, setChartTypeSolicitudes] = useState("pie");
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    //   const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [isShareEmailModalOpen, setIsShareEmailModalOpen] = useState(false);
    const [fileType, setFileType] = useState("xls");
    const [shareType, setShareType] = useState("png");
    const shareEmailModalRef = useRef(null);
    const chartScrollRef = useRef(null);
    const chartSettingsRef = useRef(null);
    const chartSettingsCotosRef = useRef(null);
    const chartSettingsAccesosRef = useRef(null);
    const chartSettingsSolicitudesRef = useRef(null);
    const pageContentRef = useRef(null);

    const [dateRange, setDateRange] = useState(
        globalFilters.dateRange || {
            startDate: "2024-09-17",
            endDate: "2025-10-20",
        },
    );

    useEffect(() => {
        saveFilterSection("global", {
            dateRange,
            selectedProvincia,
            selectedCoto,
        });
    }, [dateRange, selectedProvincia, selectedCoto]);

    useEffect(() => {
        saveFilterSection("usuarios", {
            selectedPeriod,
            selectedPeriodSolicitudes,
        });
    }, [selectedPeriod, selectedPeriodSolicitudes]);

    // Estados para datos del backend
    const [distribucionRol, setDistribucionRol] = useState(
        initialDistribucionRol,
    );
    const [usuariosData, setUsuariosData] = useState(initialUsuariosData);
    const [cotosData, setCotosData] = useState(initialCotosData);
    const [cotosPorTitular, setCotosPorTitular] = useState(
        initialCotosPorTitular,
    );
    const [accesosData, setAccesosData] = useState(initialAccesosData);
    const [distribucionAccesos, setDistribucionAccesos] = useState(
        initialDistribucionAccesos,
    );
    const [solicitudesData, setSolicitudesData] = useState(
        initialSolicitudesData,
    );
    const [distribucionSolicitudes, setDistribucionSolicitudes] = useState(
        initialDistribucionSolicitudes,
    );
    const [isLoading, setIsLoading] = useState(false);
    const [hiddenSolicitudes, setHiddenSolicitudes] = useState([]);

    const tabs = useMemo(
        () => [
            { id: "general", label: "General" },
            { id: "usuarios", label: "Usuarios" },
            { id: "autorizaciones", label: "Autorizaciones" },
            { id: "cupos", label: "Cupos" },
            { id: "capturas", label: "Capturas" },
        ],
        [],
    );

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        if (tab === "general") navigate("/general");
        if (tab === "autorizaciones") navigate("/autorizaciones");
        if (tab === "cupos") navigate("/cupos");
        if (tab === "capturas") navigate("/capturas");
        if (tab === "usuarios") navigate("/usuarios");
    }, [navigate]);

    const periodOptions = useMemo(
        () => [
            { value: "mensual", label: "Mensual" },
            { value: "anual", label: "Anual" },
            { value: "semanal", label: "Semanal" },
        ],
        [],
    );

    // Función para convertir fecha ISO a formato DD/MM/YYYY
    const formatDateForPicker = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Función para descargar en Excel
    const downloadExcel = async () => {
        try {
            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();

            // Hoja 1: Resumen General
            const wsResumen = workbook.addWorksheet("Resumen");
            wsResumen.addRow(["Panel de Datos - Usuarios"]);
            wsResumen.addRow(["Fecha de generación:", new Date().toLocaleDateString("es-ES")]);
            wsResumen.addRow([
                "Rango de fechas:",
                `${formatDateForPicker(dateRange.startDate)} - ${formatDateForPicker(dateRange.endDate)}`,
            ]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Métricas Principales"]);
            wsResumen.addRow(["Total Usuarios", usuariosData.total_usuarios]);
            wsResumen.addRow(["Total Cazadores", usuariosData.total_cazadores]);
            wsResumen.addRow(["Porcentaje Cazadores (%)", usuariosData.porcentaje_cazadores]);
            wsResumen.addRow(["Total Cotos", cotosData.total_cotos]);
            wsResumen.addRow(["Total Titulares", cotosData.total_titulares]);
            wsResumen.addRow(["Porcentaje Titulares (%)", cotosData.porcentaje_titulares]);
            wsResumen.addRow(["Total Solicitudes Cotos", solicitudesData.total_solicitudes]);

            // Hoja 2: Distribución por Rol
            if (distribucionRol.length > 0) {
                const wsRol = workbook.addWorksheet("Distribución por Rol");
                wsRol.addRow(["Rol", "Porcentaje (%)"]);
                distribucionRol.forEach((item) => {
                    wsRol.addRow([item.name, item.value]);
                });
            }

            // Hoja 3: Cotos por Titular
            if (cotosPorTitular.length > 0) {
                const wsCotos = workbook.addWorksheet("Cotos por Titular");
                wsCotos.addRow(["Cantidad de Cotos", "Número de Titulares"]);
                cotosPorTitular.forEach((item) => {
                    wsCotos.addRow([
                        item.name + " coto" + (item.name === "1" ? "" : "s"),
                        item.value,
                    ]);
                });
            }

            // Hoja 4: Distribución de Solicitudes
            if (distribucionSolicitudes.length > 0) {
                const wsSolicitudes = workbook.addWorksheet("Solicitudes");
                wsSolicitudes.addRow(["Estado", "Cantidad"]);
                distribucionSolicitudes.forEach((item) => {
                    wsSolicitudes.addRow([item.name, item.value]);
                });
            }

            const fileName = `usuarios_${new Date().toISOString().split("T")[0]}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        } catch (error) {
            // No capturar en Sentry si es un error 401 (no autorizado)
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error exportando Excel: " + error.message));
            }
            alert("Error al generar el archivo Excel. Por favor, intente de nuevo.");
            console.error("Error en downloadExcel:", error);
        }
    };

    // Función para convertir imagen a base64
    const imageToBase64 = (img) => {
        return new Promise((resolve) => {
            if (!img.src || img.src.startsWith("data:")) {
                resolve(null);
                return;
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const image = new Image();
            image.crossOrigin = "anonymous";

            image.onload = () => {
                canvas.width = image.naturalWidth || image.width;
                canvas.height = image.naturalHeight || image.height;
                ctx.drawImage(image, 0, 0);
                try {
                    const base64 = canvas.toDataURL("image/png");
                    resolve(base64);
                } catch (e) {
                    resolve(null);
                }
            };

            image.onerror = () => {
                resolve(null);
            };

            const separator = img.src.includes("?") ? "&" : "?";
            image.src = img.src + separator + "t=" + Date.now();
        });
    };

    // Función para descargar en PDF
    const downloadPDF = async () => {
        if (!pageContentRef.current) return;

        setIsDownloadModalOpen(false);
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
            // Imports dinámicos para mejor rendimiento
            const [html2canvasModule, jsPDFModule] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);
            const html2canvas = html2canvasModule.default;
            const jsPDF = jsPDFModule.default;

            const loadingDiv = document.createElement("div");
            loadingDiv.id = "pdf-loading";
            loadingDiv.style.cssText =
                "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;";
            loadingDiv.innerHTML =
                '<div style="background: white; padding: 20px 40px; border-radius: 8px; font-size: 16px;">Generando PDF...</div>';
            document.body.appendChild(loadingDiv);

            const images = pageContentRef.current.querySelectorAll("img");
            const originalSrcs = new Map();

            for (const img of images) {
                if (img.src && !img.src.startsWith("data:")) {
                    originalSrcs.set(img, img.src);
                    const base64 = await imageToBase64(img);
                    if (base64) {
                        img.src = base64;
                    }
                }
            }

            await new Promise((resolve) => setTimeout(resolve, 100));

            const canvas = await html2canvas(pageContentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: "#f5f5f5",
                imageTimeout: 0,
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation:
                    canvas.width > canvas.height ? "landscape" : "portrait",
                unit: "mm",
                format: "a4",
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;

            // * Quitamos el titulo por ahora *
            // pdf.setFontSize(16);
            // pdf.text("Panel de Datos - Usuarios", pdfWidth / 2, 10, {
            //     align: "center",
            // });

            const scaledHeight = imgHeight * ratio;
            const pageContentHeight = pdfHeight - 20;

            if (scaledHeight <= pageContentHeight) {
                pdf.addImage(
                    imgData,
                    "PNG",
                    imgX,
                    15,
                    imgWidth * ratio,
                    imgHeight * ratio,
                );
            } else {
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let isFirstPage = true;

                while (remainingHeight > 0) {
                    if (!isFirstPage) {
                        pdf.addPage();
                    }

                    const pageHeight = pageContentHeight / ratio;
                    const sliceHeight = Math.min(pageHeight, remainingHeight);

                    const tempCanvas = document.createElement("canvas");
                    tempCanvas.width = imgWidth;
                    tempCanvas.height = sliceHeight;
                    const tempCtx = tempCanvas.getContext("2d");
                    tempCtx.drawImage(
                        canvas,
                        0,
                        sourceY,
                        imgWidth,
                        sliceHeight,
                        0,
                        0,
                        imgWidth,
                        sliceHeight,
                    );

                    const tempImgData = tempCanvas.toDataURL("image/png");
                    pdf.addImage(
                        tempImgData,
                        "PNG",
                        imgX,
                        isFirstPage ? 15 : 10,
                        imgWidth * ratio,
                        sliceHeight * ratio,
                    );

                    sourceY += sliceHeight;
                    remainingHeight -= sliceHeight;
                    isFirstPage = false;
                }
            }

            pdf.setFontSize(10);
            pdf.text(
                `Generado el ${new Date().toLocaleDateString("es-ES")} - Rango: ${formatDateForPicker(dateRange.startDate)} a ${formatDateForPicker(dateRange.endDate)}`,
                pdfWidth / 2,
                pdfHeight - 5,
                { align: "center" },
            );

            const fileName = `usuarios_${new Date().toISOString().split("T")[0]}.pdf`;
            pdf.save(fileName);

            for (const [img, originalSrc] of originalSrcs) {
                img.src = originalSrc;
            }

            document.body.removeChild(loadingDiv);
        } catch (error) {
            // No capturar en Sentry si es un error 401 (no autorizado)
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error generating PDF: " + error.message));
            }
            const loadingDiv = document.getElementById("pdf-loading");
            if (loadingDiv) {
                document.body.removeChild(loadingDiv);
            }
            alert("Error al generar el PDF. Por favor, intente de nuevo.");
        }
    };

    // Función para manejar la descarga según el tipo seleccionado
    const handleDownload = () => {
        if (fileType === "xls") {
            downloadExcel();
            setIsDownloadModalOpen(false);
        } else if (fileType === "pdf") {
            downloadPDF();
        }
    };

    // Cargar todos los datos de usuarios
    const loadUsuariosData = async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            });

            // Añadir filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            // Cargar todos los datos en paralelo
            const [usuariosDataRes, cotosDataRes, solicitudesDataRes, distribucionRolRes, cotosPorTitularRes, distribucionSolicitudesRes] = await Promise.all([
                api.get("/usuarios/data/usuarios?" + params),
                api.get("/usuarios/data/cotos?" + params),
                api.get("/usuarios/data/solicitudes-cotos?" + params),
                api.get("/usuarios/data/distribucion-rol?" + params),
                api.get("/usuarios/data/cotos-titular-chart?" + params),
                api.get("/usuarios/data/distribucion-solicitudes?" + params),
            ]);

            // Actualizar estados con los datos recibidos
            // console.log('Usuarios data response:', usuariosDataRes);
            // console.log('Cotos data response:', cotosDataRes);
            // console.log('Solicitudes data response:', solicitudesDataRes);
            if (usuariosDataRes.data) {
                setUsuariosData(usuariosDataRes.data || initialUsuariosData);
            }
            if (cotosDataRes.data) {
                setCotosData(cotosDataRes.data || initialCotosData);
            }
            if (solicitudesDataRes.data) {
                setSolicitudesData(solicitudesDataRes.data || initialSolicitudesData);
            }

            setDistribucionRol(distribucionRolRes.data || []);
            setCotosPorTitular(cotosPorTitularRes.data || []);
            setDistribucionSolicitudes(distribucionSolicitudesRes.data || []);
        } catch (error) {
            // No capturar en Sentry si es un error 401 (no autorizado)
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading usuarios data: " + error.message));
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Cargar datos de distribución de accesos
    const loadDistribucionAccesos = async () => {
        try {
            const params = new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                period: selectedPeriod,
            });

            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get(
                "/usuarios/data/distribucion-accesos?" + params,
            );
            setDistribucionAccesos(response.data || []);
        } catch (error) {
            // No capturar en Sentry si es un error 401 (no autorizado)
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading distribucion accesos: " + error.message));
            }
        }
    };

    // Efecto para cargar datos principales cuando cambian los filtros
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            loadUsuariosData();
        }
    }, [dateRange.startDate, dateRange.endDate, selectedProvincia, selectedCoto]);

    // Efecto para cargar datos de accesos cuando cambia el período
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            loadDistribucionAccesos();
        }
    }, [dateRange.startDate, dateRange.endDate, selectedPeriod, selectedProvincia, selectedCoto]);

    // Colores para los gráficos
    const pieColors = useMemo(
        () => [
            "#839998",
            "#1C8153",
            "#CFC5B6",
            "#064340",
            "#988A74",
            "#DFF0E7",
        ],
        [],
    );

    const barColors = useMemo(
        () => [
            "#839998",
            "#1C8153",
            "#CFC5B6",
            "#064340",
            "#988A74",
            "#DFF0E7",
        ],
        [],
    );

    // Configuración del gráfico de distribución por rol (Pie/Doughnut)
    const distribucionRolChartData = useMemo(
        () => ({
            labels: distribucionRol.map((item) => item.name),
            datasets: [
                {
                    data: distribucionRol.map((item) => item.value),
                    backgroundColor: pieColors.slice(0, distribucionRol.length),
                    borderWidth: 0,
                },
            ],
        }),
        [distribucionRol, pieColors],
    );

    const pieChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "right",
                    labels: {
                        boxWidth: 12,
                        padding: 8,
                        font: { size: 11 },
                    },
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            return context.label + ": " + context.parsed + "%";
                        },
                    },
                },
            },
        }),
        [],
    );

    // Configuración del gráfico de cotos por titular (Barras)
    const cotosPorTitularChartData = useMemo(
        () => ({
            labels: cotosPorTitular.map(
                (item) => item.name + " coto" + (item.name === "1" ? "" : "s"),
            ),
            datasets: [
                {
                    label: "Titulares",
                    data: cotosPorTitular.map((item) => item.value),
                    backgroundColor: barColors[1],
                    borderColor: "rgba(45, 106, 79, 1)",
                    borderWidth: 1,
                },
            ],
        }),
        [cotosPorTitular, barColors],
    );

    const barChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                title: { display: false },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: { stepSize: 100 },
                },
            },
        }),
        [],
    );

    // Configuración del gráfico de distribución de accesos (Líneas)
    const distribucionAccesosChartData = useMemo(
        () => ({
            labels: distribucionAccesos.map((item) => item.name),
            datasets: [
                {
                    label: "Sesiones",
                    data: distribucionAccesos.map((item) => item.sesiones),
                    borderColor: "#2D6A4F",
                    backgroundColor: "rgba(45, 106, 79, 0.2)",
                    tension: 0.1,
                    fill: false,
                },
                {
                    label: "Visitas",
                    data: distribucionAccesos.map((item) => item.visitas),
                    borderColor: "#95D5B2",
                    backgroundColor: "rgba(149, 213, 178, 0.2)",
                    tension: 0.1,
                    fill: false,
                },
            ],
        }),
        [distribucionAccesos],
    );

    const lineChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                    labels: {
                        boxWidth: 12,
                        padding: 8,
                        font: { size: 11 },
                    },
                },
                title: { display: false },
            },
            scales: {
                y: {
                    beginAtZero: true,
                },
            },
        }),
        [],
    );

    // Configuración del gráfico de distribución de solicitudes (Pie)
    const distribucionSolicitudesChartData = {
        labels: distribucionSolicitudes.map((item) => item.name),
        datasets: [
            {
                data: distribucionSolicitudes.map((item, index) =>
                    hiddenSolicitudes.includes(index) ? 0 : item.value,
                ),
                backgroundColor: [
                    "#839998",
                    "#1C8153",
                    "#CFC5B6",
                    "#064340",
                ].map((color, index) =>
                    hiddenSolicitudes.includes(index) ? "transparent" : color,
                ),
                borderWidth: 0,
            },
        ],
    };

    // Opciones específicas para el gráfico de barras de solicitudes
    const solicitudesBarChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: "bottom",
                labels: {
                    boxWidth: 12,
                    padding: 8,
                    font: { size: 11 },
                    generateLabels: function (chart) {
                        const data = chart.data;
                        if (!data.labels.length || !data.datasets.length)
                            return [];

                        return distribucionSolicitudes.map((item, i) => {
                            const hidden = hiddenSolicitudes.includes(i);
                            const color = [
                                "#839998",
                                "#1C8153",
                                "#CFC5B6",
                                "#064340",
                            ][i];

                            return {
                                text: `${item.name}: ${formatNumber(item.value)}`,
                                fillStyle: hidden ? "#ccc" : color,
                                strokeStyle: hidden ? "#ccc" : color,
                                hidden: hidden,
                                index: i,
                            };
                        });
                    },
                },
                onClick: function (e, legendItem, legend) {
                    const index = legendItem.index;
                    setHiddenSolicitudes((prev) => {
                        if (prev.includes(index)) {
                            return prev.filter((i) => i !== index);
                        } else {
                            return [...prev, index];
                        }
                    });
                },
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        if (hiddenSolicitudes.includes(context.dataIndex)) {
                            return null;
                        }
                        const originalValue =
                            distribucionSolicitudes[context.dataIndex]?.value ||
                            0;
                        return context.label + ": " + formatNumber(originalValue);
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { stepSize: 1 },
            },
        },
    };



    return (
        <>
            {loading && <LoadingScreen />}
            {error && <div className="error-message">Error: {error}</div>}
            {!loading && !error && (
        <div className="page-usuarios" ref={pageContentRef}>
            <h1 className="page-title">Panel de datos del Sistema de control telemático de capturas en los cotos de caza de Castilla y León</h1>

            <div className="page-tabs">
                <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </div>

            <div className="page-content">
                <div className="page-filters">
                    <div style={{display: 'flex', gap: '35px', flexWrap: 'wrap'}}>
                        <div className="filter-group">
                            <label className="filter-label">
                                Seleccione un rango de fechas
                            </label>
                            <DateRangePicker
                                startDate={formatDateForPicker(dateRange.startDate)}
                                endDate={formatDateForPicker(dateRange.endDate)}
                                onChange={setDateRange}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Provincia
                            </label>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={setSelectedProvincia}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Coto
                            </label>
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={setSelectedCoto}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                    </div>
                    <div className="filter-actions">
                        <Button
                            variant="accent"
                            onClick={() => setIsDownloadModalOpen(true)}
                        >
                            <img
                                src={iconDownload}
                                alt=""
                                className="button-icon button-icon-white"
                            />
                            <span>Descargar</span>
                        </Button>
                        {/* <Button variant="primary" onClick={() => setIsShareModalOpen(true)}>
              <img src={iconShare} alt="" />
            </Button> */}
                    </div>
                </div>

                {(() => {
                    const hasFilters = selectedProvincia.length > 0 || selectedCoto.length > 0;
                    const totalCazadores = usuariosData.total_cazadores ?? 0;
                    const totalUsuarios = usuariosData.total_usuarios ?? usuariosData.total_users ?? 0;

                    const renderTrend = (pct) => {
                        if (!pct) return null;
                        const isUp = pct > 0;
                        return (
                            <span
                                className="kpi-trend"
                                style={{ color: isUp ? "#1C8153" : "#d64043" }}
                            >
                                <img src={isUp ? iconTriangleUp : iconTriangleDown} alt="" />
                                {Math.abs(pct)}%
                            </span>
                        );
                    };

                    return (
                        <>
                            {/* KPI principales */}
                            <div className="panel-section-title">Resumen</div>
                            <div className="panel-kpi-row">
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">Total usuarios</span>
                                    <span className="kpi-value">{formatNumber(totalUsuarios)}</span>
                                    <span className="kpi-foot">Todos los roles del sistema</span>
                                </div>
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">Cazadores</span>
                                    <span className="kpi-value">{formatNumber(totalCazadores)}</span>
                                    <span className="kpi-foot">
                                        {totalUsuarios > 0
                                            ? Math.round((totalCazadores / totalUsuarios) * 100) + "% del total"
                                            : ""}
                                    </span>
                                </div>
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">Total cotos</span>
                                    <span className="kpi-value">{formatNumber(cotosData.total_cotos)}</span>
                                    <span className="kpi-foot">
                                        {cotosData.total_titulares
                                            ? formatNumber(cotosData.total_titulares) + " titulares"
                                            : ""}
                                    </span>
                                </div>
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">Gestores / Titulares</span>
                                    <span className="kpi-value">{formatNumber(cotosData.total_titulares)}</span>
                                    <span className="kpi-foot">
                                        {cotosData.porcentaje_titulares
                                            ? Math.round(cotosData.porcentaje_titulares) + "% del total"
                                            : ""}
                                    </span>
                                </div>
                            </div>

                            {/* Gráficos */}
                            <div className="panel-section-title">Análisis</div>
                            <div className="panel-charts-grid">
                                {!hasFilters && (
                                    <Card>
                                        <div className="card-section">
                                            <div className="card-section-header">
                                                <p className="card-section-title">Distribución por rol</p>
                                                {/* <div className="card-actions">
                                                    <Button
                                                        variant="primary"
                                                        size="s"
                                                        onClick={() => setIsModalOpen(true)}
                                                    >
                                                        <img src={iconCog} alt="" />
                                                    </Button>
                                                </div> */}
                                            </div>
                                            <div
                                                className="chart-container-large card-section-chart"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    height: "220px",
                                                }}
                                            >
                                                {distribucionRol.length > 0 ? (
                                                    chartTypeRol === "pie" ? (
                                                        <Pie data={distribucionRolChartData} options={pieChartOptions} />
                                                    ) : (
                                                        <Doughnut data={distribucionRolChartData} options={pieChartOptions} />
                                                    )
                                                ) : (
                                                    <div style={{ color: "var(--neutral-500)" }}>
                                                        No hay datos disponibles
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                )}

                                <Card>
                                    <div className="card-section">
                                        <div className="card-section-header">
                                            <p className="card-section-title">Cotos por titular/gestor</p>
                                            <div className="card-actions">
                                                <Button
                                                    variant="primary"
                                                    size="s"
                                                    onClick={() => setIsModalCotosTitularOpen(true)}
                                                >
                                                    <img src={iconCog} alt="" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div
                                            className="chart-container-large card-section-chart"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: "220px",
                                            }}
                                        >
                                            {cotosPorTitular.length > 0 ? (
                                                chartTypeCotosTitular === "barras" ? (
                                                    <Bar data={cotosPorTitularChartData} options={barChartOptions} />
                                                ) : (
                                                    <Line
                                                        data={{
                                                            labels: cotosPorTitular.map(
                                                                (item) =>
                                                                    item.name + " coto" + (item.name === "1" ? "" : "s"),
                                                            ),
                                                            datasets: [
                                                                {
                                                                    label: "Titulares",
                                                                    data: cotosPorTitular.map((item) => item.value),
                                                                    borderColor: "#2D6A4F",
                                                                    backgroundColor: "rgba(45, 106, 79, 0.2)",
                                                                    tension: 0.1,
                                                                },
                                                            ],
                                                        }}
                                                        options={{
                                                            ...barChartOptions,
                                                            plugins: { ...barChartOptions.plugins, legend: { display: false } },
                                                        }}
                                                    />
                                                )
                                            ) : (
                                                <div style={{ color: "var(--neutral-500)" }}>
                                                    No hay datos disponibles
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>

                                {!hasFilters && (
                                    <Card>
                                        <div className="card-section">
                                            <div className="card-section-header">
                                                <p className="card-section-title">Distribución de solicitudes</p>
                                            </div>
                                            <div
                                                className="chart-container-large card-section-chart"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    height: "220px",
                                                }}
                                            >
                                                {distribucionSolicitudes.length > 0 ? (
                                                    <Bar
                                                        data={distribucionSolicitudesChartData}
                                                        options={solicitudesBarChartOptions}
                                                    />
                                                ) : (
                                                    <div style={{ color: "var(--neutral-500)" }}>
                                                        No hay datos disponibles
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                )}
                            </div>
                        </>
                    );
                })()}
            </div>

            {/* Modal: Configuración gráfico distribución por rol */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onApply={() => {
                    if (chartSettingsRef.current) {
                        chartSettingsRef.current.apply();
                    }
                }}
            >
                <ChartSettings
                    ref={chartSettingsRef}
                    chartType={chartTypeRol}
                    onChartTypeChange={setChartTypeRol}
                />
            </Modal>

            {/* Modal: Configuración gráfico cotos por titular */}
            <Modal
                isOpen={isModalCotosTitularOpen}
                onClose={() => setIsModalCotosTitularOpen(false)}
                onApply={() => {
                    if (chartSettingsCotosRef.current) {
                        chartSettingsCotosRef.current.apply();
                    }
                }}
            >
                <ChartSettings
                    ref={chartSettingsCotosRef}
                    chartType={chartTypeCotosTitular}
                    onChartTypeChange={setChartTypeCotosTitular}
                />
            </Modal>

            {/* Modal: Configuración gráfico accesos */}
            <Modal
                isOpen={isModalAccesosOpen}
                onClose={() => setIsModalAccesosOpen(false)}
                onApply={() => {
                    if (chartSettingsAccesosRef.current) {
                        chartSettingsAccesosRef.current.apply();
                    }
                }}
            >
                <ChartSettings
                    ref={chartSettingsAccesosRef}
                    chartType={chartTypeAccesos}
                    onChartTypeChange={setChartTypeAccesos}
                />
            </Modal>

            {/* Modal: Configuración gráfico solicitudes */}
            <Modal
                isOpen={isModalSolicitudesOpen}
                onClose={() => setIsModalSolicitudesOpen(false)}
                onApply={() => {
                    if (chartSettingsSolicitudesRef.current) {
                        chartSettingsSolicitudesRef.current.apply();
                    }
                }}
            >
                <ChartSettings
                    ref={chartSettingsSolicitudesRef}
                    chartType={chartTypeSolicitudes}
                    onChartTypeChange={setChartTypeSolicitudes}
                />
            </Modal>

            {/* Modal: Descargar */}
            <Modal
                isOpen={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                onApply={handleDownload}
                title="Descargar"
            >
                <DownloadSettings
                    fileType={fileType}
                    onFileTypeChange={setFileType}
                />
            </Modal>

            {/* Modal: Compartir */}
            {/* <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onApply={() => {
          setIsShareModalOpen(false)
          setIsShareEmailModalOpen(true)
        }}
        title="Compartir"
      >
        <ShareSettings
          shareType={shareType}
          onShareTypeChange={setShareType}
        />
      </Modal> */}

            {/* Modal: Compartir por email */}
            <Modal
                isOpen={isShareEmailModalOpen}
                onClose={() => setIsShareEmailModalOpen(false)}
                onApply={() => {
                    if (shareEmailModalRef.current) {
                        shareEmailModalRef.current.send();
                    }
                }}
                title="Compartir"
                applyLabel="Enviar"
                cancelLabel="Cancelar"
            >
                <ShareEmailModal
                    ref={shareEmailModalRef}
                    onSend={(email) => {
                        setIsShareEmailModalOpen(false);
                        // TODO: Implementar envío de archivo por email según shareType
                    }}
                    onCancel={() => setIsShareEmailModalOpen(false)}
                />
            </Modal>
        </div>
            )}
        </>
    );
}


